import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';
import { getSourceBrandLocationAttributesEndpointIdentifier } from './use-get-source-brand-location-attributes-api';

const endpoint = 'ums/v1/crm/management/source-brand-location-attributes';
export const createSourceBrandLocationAttributeEndpointIdentifier = endpoint;

interface BodyType {
  sourceBrandLocationId: number;
  key: string;
  value: string;
  valueCast?: 'string' | 'number' | 'boolean' | 'json';
}

type TData = {
  id: number;
  message: string;
};
type TError = AxiosError<{ message: string }>;
type Options = UseMutationOptions<TData, TError, BodyType>;
type ReturnType = UseMutationResult<TData, TError, BodyType>;

export function useCreateSourceBrandLocationAttributeApi(options?: Options): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async (body: BodyType) => {
    try {
      const { data } = await http.post(endpoint, body);

      toast({
        description: 'Location attribute created successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  const { ...otherOptions } = options || {};

  return useMutationWrapper(mutationFn, {
    onSuccess: (_, variables) => {
      // Only invalidate attribute queries, not location queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [queryEndpoint, params] = query.queryKey;
          return (
            queryEndpoint === getSourceBrandLocationAttributesEndpointIdentifier &&
            typeof params === 'object' &&
            params !== null &&
            !Array.isArray(params)
          );
        },
      });

      // Invalidate specific location's attributes
      queryClient.invalidateQueries([endpoint, 'location', variables.sourceBrandLocationId]);
    },
    ...otherOptions,
  });
}
